import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';

const initialState = {
	data: {},
	loading: false,
	error: null,
	timeStamp: null,
	credentialsLoadingComplete: false,

	// 🔄 Credential Access State
	access: {},
	accessTimestamp: null,
	credentialAccessLoadingComplete: false,

	//✅ Complete states
	completeDeleteCredential: false,

	//✅ Per-credential loading map: { [credentialID]: 'update' | 'assign' | 'unassign' | 'associate' | 'delete' }
	loadingCredentialIDs: {},

	// 📡 Propagation progress: { [credentialID]: { current, total, results: [{ deviceID, status, reason? }] } }
	credentialPropagation: {},

	// 📡 Failed/dead delivery rows (pending/attention tabs of the topbar indicator)
	propagationStatus: [],
	loadingPropagationStatus: false,

	// "orgID:siteID" of the last propagation hydrate — a scope change replaces
	// credentialPropagation wholesale, the same scope merges (see hydrate reducer)
	propagationScope: null,

	// 🎫 Add Guest
	loadingAddGuest: false,

	// 📦 Bulk Upload States
	loadingCreateCredential: false,
	loadingBulkCredential: false,
	bulkCredentialError: null
};

export const credentialsSlice = createSlice({
	name: 'credentials',
	initialState,
	reducers: {
		// 🔄 LOAD
		loadCredentials: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadCredentialsSuccess: (state, action) => {
			state.loading = false;
			state.timeStamp = Date.now();
			const { orgID, data } = action.payload;
			state.data[orgID] = mergeReduxStateData(state.data[orgID] || [], data, 'credentialID');
			state.credentialsLoadingComplete = true;
		},
		loadCredentialsError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		// ➕ CREATE CREDENTIAL
		createCredential: (state) => {
			state.loadingCreateCredential = true;
			state.error = null;
		},
		createCredentialSuccess: (state) => {
			state.loadingCreateCredential = false;
			state.timeStamp = Date.now();
		},
		createCredentialError: (state, action) => {
			state.loadingCreateCredential = false;
			state.error = action.payload;
		},

		// ✏️ UPDATE CREDENTIAL
		updateCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'update';
			state.error = null;
		},
		updateCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.timeStamp = Date.now();
		},
		updateCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// ❌ DELETE CREDENTIAL
		deleteCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'delete';
			state.completeDeleteCredential = false;
			state.error = null;
		},
		deleteCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.completeDeleteCredential = true;
			state.timeStamp = Date.now();
		},
		deleteCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.completeDeleteCredential = false;
			state.error = action.payload.error;
		},

		// 🎴 ASSIGN CREDENTIAL
		assignCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'assign';
			state.error = null;
		},
		assignCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.timeStamp = Date.now();
		},
		assignCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// ❌ UNASSIGN CREDENTIAL
		unassignCredential: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'unassign';
			state.error = null;
		},
		unassignCredentialSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.timeStamp = Date.now();
		},
		unassignCredentialError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// 🔗 ASSOCIATE CARD (card-specific)
		associateCard: (state, action) => {
			const { credentialID } = action.payload;
			state.loadingCredentialIDs[credentialID] = 'associate';
			state.error = null;
		},
		associateCardSuccess: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.timeStamp = Date.now();
		},
		associateCardError: (state, action) => {
			const { credentialID } = action.payload;
			delete state.loadingCredentialIDs[credentialID];
			state.error = action.payload.error;
		},

		// 🔄 LOAD CREDENTIAL ACCESS
		getCredentialAccess: (state) => {
			state.loading = true;
			state.error = null;
		},
		getCredentialAccessSuccess: (state, action) => {
			state.loading = false;
			state.accessTimestamp = Date.now();
			const { orgID, data } = action.payload;
			state.access[orgID] = data;
			state.credentialAccessLoadingComplete = true;
		},
		getCredentialAccessError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		// 🏢 UPDATE CREDENTIAL SITES
		updateCredentialSites: (state) => {
			state.error = null;
		},
		updateCredentialSitesSuccess: (state) => {
			state.timeStamp = Date.now();
		},
		updateCredentialSitesError: (state, action) => {
			state.error = action.payload;
		},

		// 📦 BATCH IR CARDS
		createCardsBatchIR: (state) => {
			state.loadingBulkCredential = true;
			state.bulkCredentialError = null;
		},
		createCardsBatchIRSuccess: (state) => {
			state.loadingBulkCredential = false;
			state.timeStamp = Date.now();
		},
		createCardsBatchIRError: (state, action) => {
			state.loadingBulkCredential = false;
			state.bulkCredentialError = action.payload;
		},

		// 📡 CREDENTIAL PROPAGATION — queued/started/progress/complete lifecycle
		credentialPropagationQueued: (state, action) => {
			const { credentialID, personID, total, type } = action.payload;
			if (!credentialID) return;
			const existing = state.credentialPropagation[credentialID];
			// If a retry is already in progress, ignore — preserve its state entirely
			if (existing && existing._retry && !existing.complete) return;
			state.credentialPropagation[credentialID] = {
				current: 0,
				total,
				results: [],
				queued: true,
				type: type || 'assign',
				personID: personID || null,
				_touchedAt: Date.now()
			};
		},
		credentialPropagationStarted: (state, action) => {
			const { credentialID } = action.payload;
			if (!credentialID || !state.credentialPropagation[credentialID]) return;
			state.credentialPropagation[credentialID].queued = false;
		},
		credentialPropagationProgress: (state, action) => {
			const { credentialID, deviceID, current, total, status, siteID, reason } = action.payload;
			if (!credentialID) return;
			if (!state.credentialPropagation[credentialID]) {
				state.credentialPropagation[credentialID] = { current: 0, total: 0, results: [] };
			}
			const entry = state.credentialPropagation[credentialID];
			// Preserve retry-scoped total; accept backend total for normal entries
			if (!entry._retry) entry.total = total;
			entry.current = entry._retry ? (entry._retryOffset || 0) + current : current;
			entry.queued = false;
			entry._touchedAt = Date.now();
			// v4 emits a first event with deviceID null (status 'started') — skip it
			if (deviceID) entry.results.push({ deviceID, status, siteID: siteID || null, ...(reason && { reason }) });
		},
		credentialPropagationComplete: (state, action) => {
			const { credentialID, succeeded, failed, failedDeviceIDs } = action.payload;
			if (credentialID && state.credentialPropagation[credentialID]) {
				state.credentialPropagation[credentialID].complete = true;
				state.credentialPropagation[credentialID].succeeded = succeeded || 0;
				state.credentialPropagation[credentialID].failed = failed || 0;
				state.credentialPropagation[credentialID].failedDeviceIDs = failedDeviceIDs || [];
			}
		},
		clearCredentialPropagation: (state, action) => {
			const { credentialID } = action.payload;
			delete state.credentialPropagation[credentialID];
		},

		// 📡 RETRY PROPAGATION
		retryCredentialPropagation: (state, action) => {
			const { credentialID, deviceIDs } = action.payload;
			if (credentialID) {
				const existing = state.credentialPropagation[credentialID];
				if (existing && deviceIDs && deviceIDs.length > 0) {
					// Partial retry: keep synced results, only reset failed devices
					const deviceSet = new Set(deviceIDs);
					const keptResults = (existing.results || []).filter(
						(r) => r.status === 'registered' && !deviceSet.has(r.deviceID)
					);
					state.credentialPropagation[credentialID] = {
						current: keptResults.length,
						total: keptResults.length + deviceIDs.length,
						results: keptResults,
						complete: false,
						failed: 0,
						failedDeviceIDs: [],
						_retry: true,
						_retryOffset: keptResults.length
					};
				} else {
					// Full retry or no previous state
					state.credentialPropagation[credentialID] = {
						current: 0,
						total: deviceIDs?.length || 0,
						results: [],
						complete: false,
						failed: 0,
						failedDeviceIDs: [],
						_retry: true
					};
				}
				// Remove retried items from propagationStatus (pending tab)
				if (deviceIDs && deviceIDs.length > 0) {
					const deviceSet = new Set(deviceIDs);
					state.propagationStatus = state.propagationStatus.filter(
						(item) => !(item.credentialID === credentialID && deviceSet.has(item.deviceID))
					);
				} else {
					state.propagationStatus = state.propagationStatus.filter(
						(item) => item.credentialID !== credentialID
					);
				}
			}
		},

		// 📡 FETCH PROPAGATION (single action — triggers GET /credentials/propagation)
		fetchPropagation: () => {},
		hydrateCredentialPropagation: (state, action) => {
			const { queued = [], active = [], completed = [], scope = null } = action.payload;

			// Build the snapshot map first
			const incoming = {};
			for (const entry of queued) {
				incoming[entry.credentialID] = {
					current: 0,
					total: entry.total,
					results: [],
					queued: true,
					type: entry.type || 'assign',
					personID: entry.personID || null,
					credentialName: entry.credentialName || null,
					credentialTypeID: entry.credentialTypeID || null
				};
			}
			for (const entry of active) {
				incoming[entry.credentialID] = {
					current: entry.current || 0,
					total: entry.total,
					results: entry.results || [],
					queued: false,
					type: entry.type || 'assign',
					personID: entry.personID || null,
					credentialName: entry.credentialName || null,
					credentialTypeID: entry.credentialTypeID || null
				};
			}
			for (const entry of completed) {
				incoming[entry.credentialID] = {
					current: entry.current || entry.total,
					total: entry.total,
					results: entry.results || [],
					complete: true,
					succeeded: entry.succeeded || 0,
					failed: entry.failed || 0,
					failedDeviceIDs: entry.failedDeviceIDs || [],
					type: entry.type || 'assign',
					personID: entry.personID || null,
					credentialName: entry.credentialName || null,
					credentialTypeID: entry.credentialTypeID || null
				};
			}

			// Stamp snapshot entries so stalled detection has a baseline
			for (const entry of Object.values(incoming)) entry._touchedAt = Date.now();

			// Org/site switch → replace wholesale so scopes never mix
			const scopeChanged = scope !== null && scope !== state.propagationScope;
			if (scope !== null) state.propagationScope = scope;
			if (scopeChanged) {
				state.credentialPropagation = incoming;
				return;
			}

			// Same scope → MERGE. The snapshot is a DB photo taken at request time
			// and can land AFTER newer socket events (or after a newer snapshot —
			// responses have no ordering guarantee). Wholesale replacement made the
			// progress bar jump backwards on every popover open / COMPLETE refetch,
			// dropped mid-unassign entries whose delivery rows were already deleted,
			// and discarded _retry accounting.
			for (const [credentialID, snap] of Object.entries(incoming)) {
				const live = state.credentialPropagation[credentialID];
				if (!live) {
					state.credentialPropagation[credentialID] = snap;
					continue;
				}
				if (live.complete) {
					// Snapshot shows a NEW run for a credential the socket saw finish
					// (e.g. a sweep re-drive) → take it; both complete → keep the
					// socket's richer per-device detail
					if (!snap.complete) state.credentialPropagation[credentialID] = snap;
					continue;
				}
				// In-flight: retry entries keep their retry-scoped totals/offsets
				if (live._retry) continue;
				// Keep whichever is further along; a completed snapshot is always ahead
				if (!snap.complete && (live.current || 0) >= (snap.current || 0)) continue;
				state.credentialPropagation[credentialID] = snap;
			}
			// Live entries absent from the snapshot are kept on purpose: a
			// mid-unassign credential's rows are already deleted (its COMPLETE
			// arrives via socket) and completed cards sit on auto-clear timers.
		},

		// 📡 PROPAGATION STATUS (populated by fetchPropagation response)
		fetchPropagationStatusSuccess: (state, action) => {
			state.propagationStatus = action.payload;
			state.loadingPropagationStatus = false;
		},
		fetchPropagationStatusError: (state) => {
			state.loadingPropagationStatus = false;
		},

		// 🎫 ADD GUEST (orchestrates grant/revoke + credential assign/update)
		addGuest: (state) => {
			state.loadingAddGuest = true;
			state.error = null;
		},
		addGuestSuccess: (state) => {
			state.loadingAddGuest = false;
			state.timeStamp = Date.now();
		},
		addGuestError: (state, action) => {
			state.loadingAddGuest = false;
			state.error = action.payload.error;
		}
	}
});

export const credentialsActions = credentialsSlice.actions;
export default credentialsSlice.reducer;
